import React from 'react';
import '../styles/ReportMini.css';

/**
 * A report, at dashboard-card size.
 *
 * Twelve report cards that each showed four numbers and a sentence were indistinguishable —
 * the dashboard read as one panel repeated twelve times, and you had to open each to find out
 * which was which. This draws a **miniature of the report's own primary block**, so a card is
 * recognisable as *itself* from across the screen: a trend is a sparkline, a scorecard is a
 * ring, a watchlist is its top rows, a digest is its latest lines.
 *
 * It renders from the SAME payload the full view uses (the card already holds it), so a
 * miniature can never show something the report does not. Nothing here re-derives a figure:
 * every number drawn is one the server already computed.
 *
 * Two rules it keeps from the full view, because a small chart is exactly where they get
 * dropped:
 *
 *   * **An empty report says so.** A chart with no marks reads as "everything is zero", which
 *     is a claim. The empty block's own message is shown instead.
 *   * **A value the server could not determine stays an em dash**, never a zero — the KPI
 *     tiles carry `v` through untouched.
 */

/* The block types worth drawing small, in the order they are preferred. A table or a feed is
   legible at this size but says less at a glance than a shape does, so a report carrying both
   draws the shape. */
const VISUAL = ['line', 'donut', 'columns', 'bars', 'funnel'];
const SECONDARY = ['table', 'feed', 'alerts'];

/* ⚠️ A `split` carries no shape of its own — the shapes are its children. Without flattening,
   a report laid out in two columns offers this picker nothing but layout containers, and every
   card of it draws blank: indistinguishable on the dashboard from a report with no data. */
function flatten(blocks) {
  return (blocks || []).flatMap((b) => (b && b.type === 'split'
    ? flatten([].concat(...(b.cols || [])))
    : [b]));
}

function pickBlock(blocks) {
  const usable = flatten(blocks).filter(
    (b) => b && b.type !== 'empty' && b.type !== 'hero' && b.type !== 'tiles');
  for (const type of VISUAL) {
    const hit = usable.find((b) => b.type === type);
    if (hit) return hit;
  }
  for (const type of SECONDARY) {
    const hit = usable.find((b) => b.type === type);
    if (hit) return hit;
  }
  // Last resort: whatever the report led with, INCLUDING an `empty` block — its message is
  // the honest thing to show on a card for an account with nothing in it.
  return flatten(blocks)[0] || null;
}

/* ------------------------------------------------------------------ shapes */

function Spark({ block }) {
  const pts = block.points || [];
  if (pts.length < 2) return null;
  const W = 240; const H = 56;
  const ys = pts.map((p) => p.y || 0);
  const lo = Math.min(...ys); const hi = Math.max(...ys);
  // A flat series must not be drawn as a full-height wiggle: with no range, the line sits in
  // the middle rather than being scaled to noise.
  const span = hi - lo || 1;
  const x = (i) => (i * W) / (pts.length - 1);
  const y = (v) => (hi === lo ? H / 2 : H - 4 - ((v - lo) / span) * (H - 10));
  const line = pts.map((p, i) => `${x(i)},${y(p.y)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <div className="rm-spark">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <polygon points={`0,${H} ${line} ${W},${H}`} className="rm-spark-fill" />
        <polyline points={line} className="rm-spark-line" />
        <circle cx={x(pts.length - 1)} cy={y(last.y)} r="3" className="rm-spark-dot" />
      </svg>
      <span className="rm-spark-end">{last.y}{block.unit}</span>
    </div>
  );
}

function Ring({ block }) {
  const slices = (block.slices || []).filter((s) => s.v > 0);
  const total = slices.reduce((a, s) => a + s.v, 0) || 1;
  const R = 26; const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="rm-ring-wrap">
      <div className="rm-ring">
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="32" r={R} className="rm-ring-track" />
          {slices.map((s, i) => {
            const len = (s.v / total) * C;
            const el = (
              <circle key={i} cx="32" cy="32" r={R} className={`rm-ring-arc rm-t-${s.tone}`}
                      strokeDasharray={`${len} ${C}`} strokeDashoffset={-offset}
                      transform="rotate(-90 32 32)" />
            );
            offset += len;
            return el;
          })}
        </svg>
        {block.center && <b className="rm-ring-mid">{block.center.v}{block.center.unit}</b>}
      </div>
      <ul className="rm-ring-key">
        {slices.slice(0, 4).map((s, i) => (
          <li key={i}><i className={`rm-sw rm-t-${s.tone}`} />{s.label}<b>{s.v}</b></li>
        ))}
      </ul>
    </div>
  );
}

function Cols({ block }) {
  const cols = (block.cols || []).slice(-14);
  const max = Math.max(1, ...cols.map((c) => c.value || 0));
  return (
    <div className="rm-cols" aria-hidden="true">
      {cols.map((c, i) => (
        <i key={i} className={`rm-t-${block.tone || 's1'}`}
           style={{ height: `${Math.max(((c.value || 0) / max) * 100, 2)}%` }}
           title={`${c.label}: ${c.value}`} />
      ))}
    </div>
  );
}

function MiniBars({ block }) {
  const rows = (block.rows || []).slice(0, 4);
  const max = block.max
    || Math.max(1, ...rows.map((r) => (r.segments || []).reduce((a, s) => a + (s.v || 0), 0)));
  return (
    <ul className="rm-bars">
      {rows.map((r, i) => (
        <li key={i}>
          <span className="rm-bar-label" title={r.label}>{r.label}</span>
          <span className="rm-bar-track">
            {(r.segments || []).map((s, j) => (
              <i key={j} className={`rm-t-${s.tone}`}
                 style={{ width: `${((s.v || 0) / max) * 100}%` }} />
            ))}
          </span>
          <span className="rm-bar-value">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

function MiniFunnel({ block }) {
  const stages = (block.stages || []).slice(0, 5);
  return (
    <ul className="rm-funnel">
      {stages.map((s, i) => (
        <li key={i}>
          <span className="rm-funnel-bar" style={{ width: `${Math.max(s.width || 0, 8)}%` }} />
          <span className="rm-funnel-label" title={s.label}>{s.label}</span>
          <b>{s.value}</b>
        </li>
      ))}
    </ul>
  );
}

function MiniTable({ block }) {
  const rows = (block.rows || []).slice(0, 3);
  const cols = block.cols || [];
  const first = cols[0];
  // The right-hand figure: the first right-aligned column, which is the one the table is
  // sorted or judged by. Falling back to the status pill keeps a row meaningful when there
  // is no number at all.
  const value = cols.find((c) => c.align === 'r') || cols.find((c) => c.kind === 'pill');
  if (!first) return null;
  return (
    <ul className="rm-rows">
      {rows.map((r, i) => (
        <li key={i}>
          <i className={`rm-dot rm-t-${r.tone || 'mute'}`} />
          <span className="rm-row-main" title={String(r[first.k] ?? '')}>{r[first.k] ?? '—'}</span>
          {value && (
            <b className="rm-row-value">
              {r[value.k] == null ? '—' : `${r[value.k]}${value.kind === 'pct' ? '%' : ''}`}
            </b>
          )}
        </li>
      ))}
    </ul>
  );
}

function MiniFeed({ block }) {
  const items = (block.items || []).slice(0, 3);
  return (
    <ul className="rm-rows">
      {items.map((it, i) => (
        <li key={i}>
          <i className={`rm-dot rm-t-${it.tone || 'mute'}`} />
          <span className="rm-row-main">{String(it.text || '').replace(/\*\*/g, '')}</span>
          <b className="rm-row-when">{String(it.when || '').split(' ')[0]}</b>
        </li>
      ))}
    </ul>
  );
}

function MiniAlerts({ block }) {
  const items = block.items || [];
  if (!items.length) return null;
  return (
    <ul className="rm-rows">
      {items.slice(0, 3).map((a, i) => (
        <li key={i}>
          <i className={`rm-dot rm-t-${a.tone || 'mute'}`} />
          <span className="rm-row-main" title={a.title}>{a.title}</span>
        </li>
      ))}
    </ul>
  );
}

const SHAPES = {
  line: Spark, donut: Ring, columns: Cols, bars: MiniBars,
  funnel: MiniFunnel, table: MiniTable, feed: MiniFeed, alerts: MiniAlerts,
};

/* The two figures a card leads with.
 *
 * ⚠️ The top strip is EMPTY on a report whose headline is a `hero` — the full page shows it at
 * 46px instead. Reading `kpis` alone would leave those cards with a shape and no number, so
 * the hero and the first inline tile row stand in, in that order. The card is a preview: it
 * must show what the page shows, wherever the page happens to keep it. */
function cardTiles(data) {
  const top = data?.kpis || [];
  if (top.length) return top.slice(0, 2);
  const flat = flatten(data?.blocks);
  const hero = flat.find((b) => b && b.type === 'hero');
  const tiles = flat.find((b) => b && b.type === 'tiles');
  return [
    ...(hero ? [{ k: hero.sub ? String(hero.sub).split('\n')[0] : 'Overall',
                  v: hero.unit_before ? `${hero.unit}${hero.v}` : `${hero.v}${hero.unit || ''}`,
                  tone: hero.tone }] : []),
    ...((tiles?.items || [])),
  ].slice(0, 2);
}

function ReportMini({ data }) {
  const blocks = data?.blocks || [];
  const empty = blocks.length > 0 && blocks.every((b) => b.type === 'empty');
  const kpis = cardTiles(data);

  if (empty) {
    // The report's own refusal, not a chart of zeroes.
    return <div className="rm-empty">{blocks[0].message}</div>;
  }

  const block = pickBlock(blocks);
  const Shape = block && SHAPES[block.type];

  return (
    <div className="rm">
      {kpis.length > 0 && (
        <div className="rm-kpis">
          {kpis.map((k, i) => (
            <div className={`rm-kpi rm-ink-${k.tone || ''}`} key={i}>
              <b>{k.v}</b>
              <span>{k.k}</span>
            </div>
          ))}
        </div>
      )}
      <div className="rm-shape">
        {Shape ? <Shape block={block} /> : null}
      </div>
    </div>
  );
}

export default ReportMini;
